<script lang="ts">
	import type { Block } from '$models/Block/Block';
	import type { Component } from 'svelte';
	import { intersectionObserver } from '$lib/actions/intersection-observer.svelte';

	type DeferredBlockLoader = () => Promise<{ default: Component<any> }>;

	interface Props {
		block: Block;
		activationMode: 'immediate' | 'visible';
		shell: Component<any>;
		loadComponent: DeferredBlockLoader;
	}

	let { block, activationMode, shell: ShellComponent, loadComponent }: Props = $props();

	let isActivated = $state(false);
	let componentPromise = $state<Promise<Component<any>> | null>(null);
	let rootMargin = $derived(activationMode === 'visible' ? '0px' : '0px');

	function ensureComponentPromise() {
		if (!componentPromise) {
			componentPromise = loadComponent().then(({ default: component }) => component);
		}
	}

	function activate() {
		if (isActivated) {
			return;
		}

		isActivated = true;
		ensureComponentPromise();
	}

	function activationBoundary(node: HTMLElement) {
		const handleActivation = () => {
			activate();
		};

		node.addEventListener('pointerdown', handleActivation, { passive: true });
		node.addEventListener('focusin', handleActivation);

		return {
			destroy() {
				node.removeEventListener('pointerdown', handleActivation);
				node.removeEventListener('focusin', handleActivation);
			}
		};
	}
</script>

<div
	class="deferred-block"
	use:activationBoundary
	use:intersectionObserver={{
		callback: (isIntersecting) => {
			if (isIntersecting) {
				activate();
			}
		},
		rootMargin,
		once: true
	}}
>
	{#if componentPromise}
		{#await componentPromise}
			<ShellComponent {block} />
		{:then InteractiveComponent}
			{#if isActivated}
				<InteractiveComponent {block} />
			{:else}
				<ShellComponent {block} />
			{/if}
		{:catch}
			<ShellComponent {block} />
		{/await}
	{:else}
		<ShellComponent {block} />
	{/if}
</div>

<style>
	.deferred-block {
		width: 100%;
	}
</style>
